// src/App.js
import React, { useState, useEffect } from 'react';
import {
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  updateProfile,
} from 'firebase/auth';
import {
  doc, setDoc, getDoc, addDoc, collection, query, where,
  onSnapshot, updateDoc, arrayUnion, arrayRemove,
  Timestamp, deleteDoc, getDocs, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  ArrowRight, Plus, Check, Sun, Moon, Download, AlertTriangle,
  Link as LinkIcon, Mail, LogOut, Trash2, CheckCircle,
  XCircle, UserX, Star, Paintbrush, BarChart2, RefreshCw, Settings
} from 'lucide-react';

import { auth, db, functions, googleProvider, appleProvider } from './firebase';

// --- Color Palettes / Backgrounds ---
// GROUP_COLORS = top border accent for the group card on the dashboard.
const GROUP_COLORS = [
  '#d8a7a7', '#d8bba7', '#d8d2a7', '#b2d8a7', '#a7c5d8', '#a7a7d8', '#c5a7d8',
  '#e8c7c7', '#e8d5c7', '#e8e2c7', '#d0e8c7', '#c7dbe8', '#c7c7e8', '#dbc7e8',
];

// TASK_COLORS_STANDARD = available on the Clarity tier.
// Softer, more muted palette than the original saturated rainbow.
const TASK_COLORS_STANDARD = [
  '#E8A5A5', // coral
  '#F4C7A1', // peach
  '#F4E5A1', // butter
  '#B5C9A8', // sage
  '#A8C5D9', // sky
  '#B9AED1', // lavender
  '#C9BFB3', // stone
];

// TASK_COLORS_ADVANCED = Momentum tier. Adds neutrals + soft pastels.
const TASK_COLORS_ADVANCED = [
  ...TASK_COLORS_STANDARD,
  '#FFFFFF', // pure white
  '#F0D6D6', // blush
  '#F2EBD9', // cream
  '#DDE8E0', // mist
  '#D5DFEA', // cloud
  '#DAD2E6', // lilac
  '#C5B5A4', // mocha
  '#B8B8C5', // slate
];

// BACKGROUND_OPTIONS = full-page backgrounds (Momentum tier).
// Curated for a clean, minimalist aesthetic. Solids first, then gradients, then images.
const BACKGROUND_OPTIONS = [
  { type: 'default', value: 'default', name: 'Default' },

  // Solid colors (light & airy)
  { type: 'color', value: '#FAF7F0', name: 'Bone' },
  { type: 'color', value: '#E8F0F5', name: 'Pale Sky' },
  { type: 'color', value: '#E8EDE5', name: 'Sage Whisper' },
  { type: 'color', value: '#FBE9E4', name: 'Blush Mist' },
  { type: 'color', value: '#EFE9F2', name: 'Lavender Haze' },
  { type: 'color', value: '#E5E1DC', name: 'Warm Stone' },
  { type: 'color', value: '#111827', name: 'Charcoal' }, // single dark option for users who want it

  // Gradients (soft, diagonal)
  { type: 'gradient', value: 'linear-gradient(135deg, #FFE4F1 0%, #E0F2F7 100%)', name: 'Sakura Sky' },
  { type: 'gradient', value: 'linear-gradient(135deg, #E0F2F1 0%, #F5F9FA 100%)', name: 'Ocean Mist' },
  { type: 'gradient', value: 'linear-gradient(135deg, #FFF1E6 0%, #FFE4E1 100%)', name: 'Sunrise' },

  // Image backgrounds (Unsplash; replace any URL you don't love)
  { type: 'image', value: 'https://images.unsplash.com/photo-1522383225653-ed111181a951?auto=format&fit=crop&w=2070&q=80', name: 'Cherry Blossom' },
  { type: 'image', value: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=2070&q=80', name: 'Misty Mountain' },
  { type: 'image', value: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=2070&q=80', name: 'Calm Ocean' },
  { type: 'image', value: 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?auto=format&fit=crop&w=2070&q=80', name: 'Zen Garden' },
];

// --- Platform Detection ---
// Apple Sign-In is not supported on Android. Hide it there to avoid a confusing error.
const isAndroid = /android/i.test(navigator.userAgent);

// --- Helpers ---
// 2-tier model: internal subscriptionTier is "free" or "momentum". The free
// tier is DISPLAYED as "Clarity". Legacy paid "clarity" (retired) also maps to
// the "Clarity" label for any stray value. Matches iOS displayTierName(_:).
const displayTierName = (tier) => {
  switch ((tier || 'free').toLowerCase()) {
    case 'momentum': return 'Momentum';
    case 'clarity':  // legacy paid tier — no subscribers; show as Clarity
    case 'free':
    default:         return 'Clarity';
  }
};

const formatDate = (timestamp) => {
  // Guard against null, serverTimestamp sentinels (no .seconds yet on a
  // freshly-created doc), and malformed values.
  if (!timestamp || typeof timestamp.seconds !== 'number') return 'No date';
  return new Date(timestamp.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const sortTasks = (tasks) => {
  const now = new Date();
  const bucket = (t) => {
    if (t.isComplete) return 3; // completed last
    if (t.dueDate?.toDate) {
      const d = t.dueDate.toDate();
      return d < now ? 0 : 1;   // 0=overdue, 1=upcoming
    }
    return 2;                   // no due date
  };
  return [...tasks].sort((a, b) => {
    const ba = bucket(a), bb = bucket(b);
    if (ba !== bb) return ba - bb;
    if (ba === 0 || ba === 1) {
      const da = a.dueDate?.toDate?.() || new Date(8640000000000000);
      const db = b.dueDate?.toDate?.() || new Date(8640000000000000);
      return da - db;
    }
    if (ba === 2) return (a.taskName || '').localeCompare(b.taskName || '');
    return (a.taskName || '').localeCompare(b.taskName || '');
  });
};

const humanizeAuthError = (err) => {
  const code = err?.code || '';
  switch (code) {
    case 'auth/operation-not-allowed': return 'Google sign-in is disabled. Enable the Google provider in Firebase Auth.';
    case 'auth/unauthorized-domain':   return 'This domain is not authorized. Add it under Firebase Auth → Authorized domains.';
    case 'auth/popup-blocked':         return 'Popup was blocked. Trying redirect sign-in…';
    case 'auth/popup-closed-by-user':  return 'Popup closed before completing sign-in. Please try again.';
    case 'auth/cancelled-popup-request': return 'Another popup request is in progress.';
    default: return `${code || 'auth/error'}: ${err?.message || 'Sign-in failed.'}`;
  }
};

// --- App Root ---
const App = () => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Complete redirect results (if any) once on load
  useEffect(() => {
    getRedirectResult(auth).catch((err) => {
      const code = err?.code || '';
      if (code) console.warn('Redirect sign-in error:', humanizeAuthError(err));
    });
  }, []);

  // REPLACE the existing onAuthStateChanged effect with this one
  useEffect(() => {
    let unsubUserDoc = null;

    const unsubAuth = onAuthStateChanged(auth, async (currentUser) => {
      // tear down any previous userDoc listener
      if (unsubUserDoc) {
        unsubUserDoc();
        unsubUserDoc = null;
      }

      if (currentUser) {
        const userRef = doc(db, "users", currentUser.uid);

        // ensure doc exists once, then listen in realtime
        const snap = await getDoc(userRef);
        if (!snap.exists()) {
          // Capture the browser's IANA timezone (e.g. "America/Los_Angeles") so the
          // daily-reminder Cloud Function can send emails at 6am LOCAL for this user.
          let detectedTz = "America/New_York";
          try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (tz) detectedTz = tz;
          } catch (e) { /* fall back */ }

          const newUserDoc = {
            uid: currentUser.uid,
            name: currentUser.displayName || currentUser.email.split('@')[0],
            email: currentUser.email,
            createdAt: serverTimestamp(),
            dailyRemindersEnabled: false,
            subscriptionTier: "free",
            // Per-group, per-user background overrides. Map: { [groupId]: backgroundValue }.
            // Field name matches iOS (groupBackgroundPreferences) for cross-client compatibility.
            groupBackgroundPreferences: {},
            // Reminder scheduling — field names match iOS for cross-client compat.
            reminderTimeZone: detectedTz,
            reminderHour: 6,
          };
          await setDoc(userRef, newUserDoc, { merge: true });
        }

        // live updates to userData (so background changes immediately)
        unsubUserDoc = onSnapshot(
          userRef,
          (ds) => {
            setUserData(ds.data() || null);
            setUser(currentUser);
            setLoading(false);
          },
          (err) => {
            console.error("User doc listener error:", err);
            setUser(currentUser);
            setUserData(null);
            setLoading(false);
          }
        );
      } else {
        setUser(null);
        setUserData(null);
        setLoading(false);
      }
    });

    return () => {
      unsubAuth();
      if (unsubUserDoc) unsubUserDoc();
    };
  }, []);


  // Dark mode
  useEffect(() => {
    const stored = localStorage.getItem('darkMode');
    if (stored) setIsDarkMode(JSON.parse(stored));
    else setIsDarkMode(window.matchMedia?.('(prefers-color-scheme: dark)').matches);
  }, []);
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
  }, [isDarkMode]);

  if (loading) return <LoadingScreen />;

  // Main app surface is a fixed clean canvas — pure white in light mode, slate in dark.
  // Per-group background customization happens inside GroupView, not here.
  return (
    <div className="min-h-screen font-sans bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 transition-colors duration-300">
      {user && userData
        ? <Dashboard user={user} userData={userData} isDarkMode={isDarkMode} toggleDarkMode={() => setIsDarkMode(!isDarkMode)} />
        : <AuthScreen />}
    </div>
  );
};

// --- Loading ---
const LoadingScreen = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
    <div className="flex flex-col items-center">
      <img src="https://i.postimg.cc/QCdBnzfL/Black-logo-circle.png" alt="Logo" className="h-20 w-20 mb-4 animate-pulse" />
      <p className="text-lg text-gray-600 dark:text-gray-400">Loading Simple Business...</p>
    </div>
  </div>
);

// --- Auth ---
const AuthScreen = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState(''), [email, setEmail] = useState(''), [password, setPassword] = useState('');
  const [error, setError] = useState(''), [isLoading, setIsLoading] = useState(false);

  const handleOAuth = async (provider) => {
    setError('');
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      const code = err?.code || '';
      if (
        code === 'auth/popup-blocked' ||
        code === 'auth/operation-not-supported-in-this-environment' ||
        code === 'auth/cancelled-popup-request'
      ) {
        try {
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirErr) {
          setError(humanizeAuthError(redirErr));
        }
      } else {
        setError(humanizeAuthError(err));
      }
    }
  };

  const handleGoogleSignIn = () => handleOAuth(googleProvider);
  const handleAppleSignIn  = () => handleOAuth(appleProvider);

  const handleEmailAuth = async (e) => {
    e.preventDefault(); setError(''); setIsLoading(true);
    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        if (!name.trim()) throw new Error("Please enter your name.");
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(cred.user, { displayName: name.trim() });
      }
    } catch (err) {
      setError(err.message?.replace('Firebase: ', '') || 'Authentication failed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-200 dark:bg-gray-800">
      <div className="w-full max-w-md p-8 space-y-4 bg-white dark:bg-gray-900 rounded-xl shadow-2xl">
        <div className="flex flex-col items-center">
          <img src="https://i.postimg.cc/QCdBnzfL/Black-logo-circle.png" alt="Logo" className="h-16 w-16 mb-2" />
          <h1 className="text-3xl font-bold text-center text-gray-900 dark:text-white">Simple Business</h1>
          <p className="text-center text-gray-600 dark:text-gray-400">by OA</p>
        </div>

        {error && <p className="text-red-500 text-sm text-center bg-red-100 dark:bg-red-900/50 p-2 rounded-md">{error}</p>}

        <div className={`grid gap-4 ${isAndroid ? 'grid-cols-1' : 'grid-cols-2'}`}>
          <button onClick={handleGoogleSignIn} className="flex items-center justify-center gap-2 w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path><path fill="none" d="M0 0h48v48H0z"></path></svg>
            Google
          </button>
          {!isAndroid && (
            <button onClick={handleAppleSignIn} className="flex items-center justify-center gap-2 w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <img src="https://i.postimg.cc/CLxdqsSZ/Apple-icon.png" alt="Apple icon" className="w-5 h-5" />
              Apple
            </button>
          )}
        </div>

        <div className="relative flex py-2 items-center">
          <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
          <span className="flex-shrink mx-4 text-gray-500 dark:text-gray-400 text-sm">OR</span>
          <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
        </div>

        <form onSubmit={handleEmailAuth} className="space-y-4">
          {!isLogin && (
            <div><input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your Name" required className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
          )}
          <div><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email Address" required className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
          <div><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (6+ characters)" required className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
          <button type="submit" disabled={isLoading} className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md transition duration-300 disabled:bg-blue-400">
            {isLoading ? 'Processing...' : (isLogin ? 'Log In' : 'Sign Up')} <ArrowRight size={20} />
          </button>
        </form>
          <p className="text-center text-sm pt-2">
            <button
              type="button"
              onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              {isLogin ? "Don't have an account? Sign Up" : "Already have an account? Log In"}
            </button>
          </p>
      </div>
    </div>
  );
};

// --- Dashboard ---
const Dashboard = ({ user, userData, isDarkMode, toggleDarkMode }) => {
  const [groups, setGroups] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const tier = userData.subscriptionTier || 'free';
  // 2-tier model: "free" (shown as "Clarity") and "momentum".
  const tierLimits = {
    free: { groups: 2, invites: 1, tasks: 3 },
    momentum: { groups: 15, invites: 10, tasks: 20 }
  };
  const limits = tierLimits[tier] || tierLimits.free;

  useEffect(() => {
    const groupsQuery = query(
      collection(db, "groups"),
      where("members", "array-contains", user.uid)
    );
    const unsubGroups = onSnapshot(groupsQuery, (qs) => {
      const data = qs.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => a.groupName.localeCompare(b.groupName));
      setGroups(data);
      setLoading(false);
    });

    // Use the auth user's email (stable for the session) rather than userData.email
    // so this subscription doesn't churn when the user doc updates.
    const invitesQuery = query(
      collection(db, "invitations"),
      where("email", "==", user.email)
    );
    const unsubInvites = onSnapshot(invitesQuery, (qs) => {
      setInvitations(qs.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => { unsubGroups(); unsubInvites(); };
  }, [user.uid, user.email]);

  const handleAcceptInvitation = async (inv) => {
    // Route through the acceptInvitation Cloud Function so the server enforces
    // per-tier group caps atomically. Free=2, Clarity=5, Momentum=15.
    try {
      const acceptInvitation = httpsCallable(functions, 'acceptInvitation');
      await acceptInvitation({ invitationId: inv.id });
    } catch (e) {
      console.error("Accept invitation error:", e);
      // Callable errors surface as { code: 'functions/<status>', message, details }
      const code = e?.code || '';
      if (code === 'functions/resource-exhausted' || code === 'resource-exhausted') {
        // At tier cap — surface the upgrade modal instead of a generic alert.
        setShowUpgradeModal(true);
      } else if (code === 'functions/permission-denied' || code === 'permission-denied') {
        alert("This invitation isn't addressed to your email.");
      } else if (code === 'functions/not-found' || code === 'not-found') {
        alert("This invitation is no longer valid.");
      } else {
        alert("Could not accept invitation. Please try again.");
      }
    }
  };

  const handleDenyInvitation = async (invitationId) => {
    try {
      await deleteDoc(doc(db, "invitations", invitationId));
    } catch (e) {
      console.error("Deny invitation error:", e);
    }
  };

  const handleDeleteGroup = async (group) => {
    const isOwner = user.uid === group.adminId;
    const message = isOwner
      ? "Delete this group for everyone?"
      : "Leave this group?";
    if (!window.confirm(message)) return;

    if (isOwner) {
      try {
        const groupRef = doc(db, "groups", group.id);
        const tasksQ = query(collection(db, "groups", group.id, "tasks"));
        const snap = await getDocs(tasksQ);
        const batch = writeBatch(db);
        snap.forEach(td => batch.delete(td.ref));
        batch.delete(groupRef);
        await batch.commit();
      } catch (e) {
        console.error("Delete group error:", e);
      }
    } else {
      try {
        await updateDoc(doc(db, "groups", group.id), { members: arrayRemove(user.uid) });
      } catch (e) {
        console.error("Leave group error:", e);
      }
    }
  };

  const handleCreateGroupClick = () => {
    if (groups.length >= limits.groups) setShowUpgradeModal(true);
    else setShowCreateGroup(true);
  };

  // Block future invites from a specific inviter (by UID or email)
  const handleBlockInvites = async (invite) => {
    const blockUid = invite.invitedBy || null;
    const blockEmail = invite.invitedByEmail || null;

    if (!blockUid && !blockEmail) {
      alert("Cannot block: invite is missing inviter info.");
      return;
    }
    if (!window.confirm("Block future invites from this sender? You can unblock later in Settings.")) return;

    try {
      const callBlock = httpsCallable(functions, 'blockInvitesFrom');
      await callBlock({ blockUid, blockEmail });

      // remove the current invite immediately
      await deleteDoc(doc(db, "invitations", invite.id));

      alert("Blocked. You won't receive invites from this sender.");
    } catch (err) {
      console.error("Block invites error:", err);
      alert("Could not block invites. Please try again.");
    }
  };

  if (selectedGroup) {
    return (
      <GroupView
        group={selectedGroup}
        user={user}
        userData={userData}
        onBack={() => setSelectedGroup(null)}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
      />
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <header className="flex justify-between items-center p-4 mb-8 bg-white/75 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
          <img
            src="https://i.postimg.cc/QCdBnzfL/Black-logo-circle.png"
            alt="Logo"
            className="h-10 w-10"
          />
          <div>
            <h1 className="text-2xl font-bold">Dashboard</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Welcome, {userData.name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Dark mode toggle */}
          <button
            onClick={toggleDarkMode}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          {/* Upgrade (only if not Momentum) */}
          {userData.subscriptionTier !== 'momentum' && (
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 transition-colors"
            >
              <Star size={16} /> Upgrade
            </button>
          )}

          {/* Settings */}
          <button
            onClick={() => setShowSettingsModal(true)}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Open settings"
          >
            <Settings size={20} />
          </button>

          {/* Logout */}
          <button
            onClick={() => signOut(auth)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-100 dark:bg-red-900/50 rounded-lg hover:bg-red-200 dark:hover:bg-red-900 transition-colors"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      {tier === 'free' && (
        <div className="mb-8 p-4 text-center bg-yellow-100 dark:bg-yellow-900/50 rounded-lg text-yellow-800 dark:text-yellow-200">
          Advertisement Banner
        </div>
      )}

      {invitations.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Pending Invitations</h2>
          <div className="space-y-3">
            {invitations.map(invite => (
              <div
                key={invite.id}
                className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm flex justify-between items-center"
              >
                <div>
                  <p>
                    You've been invited to join <strong>{invite.groupName}</strong>
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleAcceptInvitation(invite)}
                    className="p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/50 rounded-full"
                    title="Accept"
                  >
                    <CheckCircle size={20} />
                  </button>
                  <button
                    onClick={() => handleDenyInvitation(invite.id)}
                    className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full"
                    title="Deny"
                  >
                    <XCircle size={20} />
                  </button>
                  <button
                    onClick={() => handleBlockInvites(invite)}
                    className="p-2 text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                    title="Block invites from this sender"
                  >
                    Block
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="text-xl font-semibold mb-4">My Groups</h2>
        {loading ? (
          <p>Loading groups...</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {groups.map((group, i) => (
                <div
                  key={group.id}
                  className="relative p-6 bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                  style={{ borderTop: `6px solid ${GROUP_COLORS[i % GROUP_COLORS.length]}` }}
                >
                  <div onClick={() => setSelectedGroup(group)} className="h-full">
                    <h2 className="text-xl font-semibold mb-2">{group.groupName}</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {group.members.length} member(s)
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteGroup(group)}
                    className="absolute top-2 right-2 p-1 text-red-400 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full"
                    title="Delete / Leave group"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}

              <button
                onClick={handleCreateGroupClick}
                className="flex flex-col items-center justify-center p-6 bg-gray-200 dark:bg-gray-700/50 rounded-xl border-2 border-dashed border-gray-400 dark:border-gray-600 hover:border-blue-500 hover:text-blue-500 transition-colors"
              >
                <Plus size={32} />
                <span className="mt-2 font-semibold">Create New Group</span>
              </button>
            </div>

            {groups.length === 0 && !loading && (
              <div className="text-center py-16">
                <p className="text-gray-500">You are not a member of any groups yet.</p>
                <p className="text-gray-500">Create one to get started!</p>
              </div>
            )}
          </>
        )}
      </div>

      {showCreateGroup && (
        <CreateGroupModal
          user={user}
          onClose={() => setShowCreateGroup(false)}
        />
      )}
      {showUpgradeModal && (
        <UpgradeModal
          user={user}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}
      {showSettingsModal && (
        <SettingsModal
          user={user}
          userData={userData}
          onClose={() => setShowSettingsModal(false)}
        />
      )}
    </div>
  );
};
// --- Create Group Modal ---

const CreateGroupModal = ({ user, onClose }) => {
  const [groupName, setGroupName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) return;
    setIsCreating(true);
    try {
      await addDoc(collection(db, "groups"), {
        groupName,
        adminId: user.uid,
        members: [user.uid],
        createdAt: Timestamp.now()
      });
      onClose();
    } catch (e) {
      console.error("Create group error:", e);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 w-full max-w-md">
        <h2 className="text-2xl font-bold mb-4">Create a New Group</h2>
        <form onSubmit={handleCreateGroup}>
          <input type="text" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Enter group name"
                 className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required/>
          <div className="flex justify-end gap-4 mt-6">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">Cancel</button>
            <button type="submit" disabled={isCreating} className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:bg-blue-400 transition-colors">
              {isCreating ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Group View ---
const GroupView = ({ group, user, userData, onBack, isDarkMode, toggleDarkMode }) => {
  const [members, setMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [showAddTask, setShowAddTask] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [showGantt, setShowGantt] = useState(false);
  const isAdmin = user.uid === group.adminId;

  // Daily reminders are now toggled in SettingsModal (Dashboard-level), not per-group.

  const tier = userData.subscriptionTier || 'free';
  // 2-tier model: "free" (shown as "Clarity") and "momentum".
  const tierLimits = {
    free: { groups: 2, invites: 1, tasks: 3 },
    momentum: { groups: 15, invites: 10, tasks: 20 }
  };
  const limits = tierLimits[tier] || tierLimits.free;

  useEffect(() => {
    if (group.members.length === 0) return;

    const fetchMembers = async () => {
      const memberPromises = group.members.map(uid => getDoc(doc(db, "users", uid)));
      const memberDocs = await Promise.all(memberPromises);
      const memberData = memberDocs.map(d => d.exists() ? ({ id: d.id, ...d.data() }) : ({ id: 'unknown', name: 'Unknown User' }));
      setMembers(memberData);
    };
    fetchMembers();

    const tasksQuery = query(collection(db, "groups", group.id, "tasks"));
    const unsub = onSnapshot(tasksQuery, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTasks(data);
    });
    return unsub;
  }, [group.id, group.members]);

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from the group?`)) return;
    try { await updateDoc(doc(db, "groups", group.id), { members: arrayRemove(memberId) }); }
    catch (e) { console.error("Remove member error:", e); }
  };
  const handleAddTaskClick = (memberId) => {
    const count = tasks.filter(t => t.assignedTo === memberId).length;
    if (count >= limits.tasks) setShowUpgradeModal(true);
    else setShowAddTask(memberId);
  };

  const exportToCSV = () => {
    // RFC-4180 escaping: wrap every field in quotes and double any internal
    // quotes, so commas, quotes and newlines in task text can't corrupt the CSV.
    const csvCell = (v) => {
      const s = (v === undefined || v === null) ? '' : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const header = ["Task Name", "Description", "Due Date", "URL", "Assigned To", "Status", "Created At"];
    const rows = tasks.map(task => {
      const member = members.find(m => m.id === task.assignedTo);
      return [
        task.taskName, task.description, formatDate(task.dueDate), task.url,
        member ? member.name : 'Unassigned',
        task.isComplete ? 'Complete' : 'Open', formatDate(task.createdAt),
      ].map(csvCell).join(",");
    });
    const csv = [header.map(csvCell).join(","), ...rows].join("\r\n");
    // Prepend a BOM so Excel reads UTF-8 correctly; download via Blob (the old
    // data: URI approach could break on long or special-character content).
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${group.groupName}_tasks.csv`);
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Per-user, per-group background. Stored at users/{uid}.groupBackgroundPreferences[group.id]
  // (matches iOS field name for cross-client sync).
  // Fallback chain:
  //   1. iOS-compatible per-group override map
  //   2. legacy `groupBackgrounds` map (from earlier web-only iteration)
  //   3. global default `backgroundPreference` (still honored if a user set one before)
  //   4. 'default' (no inline style applied)
  // Momentum-only feature; users on free/clarity see the default flat surface.
  const groupBgPref =
    userData?.groupBackgroundPreferences?.[group.id] ??
    userData?.groupBackgrounds?.[group.id] ??
    userData?.backgroundPreference ??
    'default';
  const groupBgStyle = {};
  if (
    userData?.subscriptionTier === 'momentum' &&
    groupBgPref &&
    groupBgPref !== 'default'
  ) {
    if (groupBgPref.startsWith('http')) {
      Object.assign(groupBgStyle, {
        backgroundImage: `url(${groupBgPref})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      });
    } else if (groupBgPref.startsWith('linear-gradient')) {
      groupBgStyle.backgroundImage = groupBgPref;
    } else {
      groupBgStyle.backgroundColor = groupBgPref;
    }
  }

  return (
    <div className="flex flex-col h-screen" style={groupBgStyle}>
      <header className="flex-shrink-0 flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700 bg-white/75 dark:bg-gray-800/50 backdrop-blur-sm sticky top-0 z-10">
        <div>
          <button onClick={onBack} className="text-sm text-blue-600 dark:text-blue-400 hover:underline mb-1">&larr; Back to Groups</button>
          <h1 className="text-2xl font-bold">{group.groupName}</h1>
        </div>
        <div className="flex items-center gap-4">
          {isAdmin && <button onClick={() => setShowInvite(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"><Mail size={16} /> Invite</button>}
          <button onClick={exportToCSV} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"><Download size={16} /> Export CSV</button>
          {userData.subscriptionTier === 'momentum' && <button onClick={() => setShowCustomizeModal(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"><Paintbrush size={16} /> Customize</button>}
{userData.subscriptionTier === 'momentum' && (
  <button
    onClick={() => setShowGantt(true)}
    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 transition-colors"
  >
    <BarChart2 size={16} /> Gantt Chart
  </button>
)}

          <button onClick={toggleDarkMode} className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </div>
      </header>

      <main className="flex-grow overflow-x-auto p-4">
        <div className="flex gap-6 min-w-max">
          {members.map(member => {
            const memberTasks = tasks.filter(t => t.assignedTo === member.id);
            const openTasksCount = memberTasks.filter(t => !t.isComplete).length;
            const isCurrentUser = member.id === user.uid;

            return (
              <div key={member.id} className="w-80 flex-shrink-0 bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold text-gray-800">{member.name}</h2>
                    {openTasksCount > 0 && <span className="flex items-center justify-center text-xs font-bold text-white bg-blue-500 rounded-full w-5 h-5">{openTasksCount}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {isAdmin && !isCurrentUser && (
                      <button onClick={() => handleRemoveMember(member.id, member.name)} title={`Remove ${member.name}`} className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full">
                        <UserX size={18} />
                      </button>
                    )}
                    <button onClick={() => handleAddTaskClick(member.id)} className="p-1 text-gray-500 hover:text-blue-500 dark:text-gray-400"><Plus size={18} /></button>
                  </div>
                </div>
                <div className="space-y-3 h-[calc(100vh-12rem)] overflow-y-auto pr-2">
                  {sortTasks(memberTasks).map(task => (
                    <TaskCard key={task.id} task={task} groupId={group.id} tier={userData.subscriptionTier} />
                  ))}
                  {memberTasks.length === 0 && <p className="text-sm text-center text-gray-500 pt-8">No tasks assigned.</p>}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {showInvite && <InviteMemberModal group={group} user={user} tier={userData.subscriptionTier} tierLimits={tierLimits} onClose={() => setShowInvite(false)} />}
      {showAddTask && <AddTaskModal groupId={group.id} memberId={showAddTask} tier={tier} onClose={() => setShowAddTask(null)} />}
      {showUpgradeModal && <UpgradeModal user={user} onClose={() => setShowUpgradeModal(false)} />}
      {showCustomizeModal && (
        <CustomizeModal
          user={user}
          groupId={group.id}
          groupName={group.groupName}
          currentBackgroundPreference={groupBgPref}
          onClose={() => setShowCustomizeModal(false)}
        />
      )}
      {showGantt && (
        <GanttModal
          groupName={group.groupName}
          members={members}
          tasks={tasks}
           onClose={() => setShowGantt(false)}
        />
      )}

    </div>
  );
};

// --- Task Card + Forms ---
const TaskCard = ({ task, groupId, tier }) => {
  const [isEditing, setIsEditing] = useState(false);
  const toggleComplete = async () => {
    await updateDoc(doc(db, "groups", groupId, "tasks", task.id), { isComplete: !task.isComplete });
  };
  const isOverdue = !task.isComplete && task.dueDate && task.dueDate.toDate() < new Date();

  const createICSFile = () => {
    if (!task.dueDate) return;
    const startDate = task.dueDate.toDate().toISOString().replace(/-|:|\.\d\d\d/g,"");
    const icsContent = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nUID:${task.id}@simplebusiness.oa\nDTSTAMP:${new Date().toISOString().replace(/-|:|\.\d\d\d/g,"")}\nDTSTART;VALUE=DATE:${startDate.substring(0,8)}\nSUMMARY:${task.taskName}\nDESCRIPTION:${task.description}\nEND:VEVENT\nEND:VCALENDAR`;
    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${task.taskName}.ics`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };
  const googleCalendarLink = () => {
    if (!task.dueDate) return;
    const startDate = task.dueDate.toDate().toISOString().substring(0,10).replace(/-/g, '');
    const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(task.taskName)}&dates=${startDate}/${startDate}&details=${encodeURIComponent(task.description)}`;
    window.open(url, '_blank');
  };

  if (isEditing) return <EditTaskForm task={task} groupId={groupId} tier={tier} onCancel={() => setIsEditing(false)} />;

  return (
    <div className={`p-3 bg-white dark:bg-gray-700 rounded-lg shadow-sm transition-all duration-300 ${task.isComplete ? 'opacity-60 bg-gray-50 dark:bg-gray-800' : ''} ${isOverdue ? 'bg-red-50 dark:bg-red-900/20' : ''}`} style={{ borderLeft: `5px solid ${task.borderColor || '#8FBC8F'}` }}>
      <div className="flex justify-between items-start">
        <h3 className="font-semibold text-sm mb-1 pr-2 flex items-center gap-1">
          {isOverdue && <AlertTriangle size={14} className="text-red-500" />}
          {task.taskName}
        </h3>
        <button onClick={toggleComplete} className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${task.isComplete ? 'bg-green-500 border-green-500' : 'border-gray-400 hover:border-green-500'}`}>
          {task.isComplete && <Check size={12} className="text-white" />}
        </button>
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 break-words">{task.description}</p>
      {task.url && (
        <a href={task.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-500 hover:underline mb-2 truncate">
          <LinkIcon size={12} /> {task.url}
        </a>
      )}
      <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
        <div>
          <p>Due: {formatDate(task.dueDate)}</p>
          <p className="mt-1">Created: {formatDate(task.createdAt)}</p>
        </div>
        <div className="flex items-center gap-1">
          {tier !== 'free' && (
            <>
              <button onClick={googleCalendarLink} title="Add to Google Calendar" className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M19 4H5C3.895 4 3 4.895 3 6V20C3 21.105 3.895 22 5 22H19C20.105 22 21 21.105 21 20V6C21 4.895 20.105 4 19 4Z" fill="#4285F4"/><path d="M3 10H21" stroke="white" strokeWidth="1.5"/><text x="12" y="18" fontFamily="sans-serif" fontSize="10" textAnchor="middle" fill="white" fontWeight="bold">G</text></svg>
              </button>
              <button onClick={createICSFile} title="Download iCalendar (.ics)" className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="4" fill="white" stroke="#E5E7EB" strokeWidth="1.5"/><rect x="3" y="4" width="18" height="6" rx="2" fill="#EF4444"/><text x="12" y="8.5" fontFamily="sans-serif" fontSize="4" textAnchor="middle" fill="white" fontWeight="bold">iCal</text><text x="12" y="18" fontFamily="sans-serif" fontSize="10" textAnchor="middle" fill="#1F2937" fontWeight="bold">25</text></svg>
              </button>
            </>
          )}
          <button onClick={() => setIsEditing(true)} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full text-xs">Edit</button>
        </div>
      </div>
    </div>
  );
};

const TaskForm = ({ onSave, onCancel, initialData = {}, isEditing = false, tier }) => {
  // Parse a YYYY-MM-DD string as a local-midnight Date (avoids the UTC drift
  // where `new Date("2026-05-16")` becomes the previous day for users west of UTC).
  const toLocalDate = (s) => {
    if (!s) return null;
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  };
  // Convert an existing Firestore Timestamp back to a YYYY-MM-DD string using LOCAL parts,
  // so editing a task doesn't shift the displayed date by a day.
  const tsToLocalYMD = (ts) => {
    if (!ts?.seconds) return '';
    const d = new Date(ts.seconds * 1000);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const [taskName, setTaskName] = useState(initialData.taskName || '');
  const [description, setDescription] = useState(initialData.description || '');
  const [dueDate, setDueDate] = useState(tsToLocalYMD(initialData.dueDate));
  const [url, setUrl] = useState(initialData.url || '');
  const [borderColor, setBorderColor] = useState(initialData.borderColor || '#8FBC8F');

  const handleSubmit = (e) => {
    e.preventDefault();
    const localDue = toLocalDate(dueDate);
    onSave({ taskName, description, dueDate: localDue ? Timestamp.fromDate(localDue) : null, url, borderColor });
  };

  // Only Momentum reaches the color picker (it's gated by tier !== 'free').
  const availableColors = TASK_COLORS_ADVANCED;

  return (
    <form onSubmit={handleSubmit} className="p-3 bg-white dark:bg-gray-700 rounded-lg shadow-sm space-y-3">
      <input type="text" placeholder="Task name" value={taskName} onChange={e => setTaskName(e.target.value)} required className="w-full text-sm p-2 rounded bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500"/>
      <textarea placeholder="Description (max 250 chars)" value={description} onChange={e => setDescription(e.target.value)} maxLength="250" className="w-full text-xs p-2 rounded bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 h-20 resize-none"></textarea>
      <input type="url" placeholder="https://example.com" value={url} onChange={e => setUrl(e.target.value)} className="w-full text-xs p-2 rounded bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500"/>
      <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="w-full text-xs p-2 rounded bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500"/>

      {tier !== 'free' && (
        <div className="flex flex-wrap gap-2">
          {availableColors.map(color => (
            <button key={color} type="button" onClick={() => setBorderColor(color)} className={`w-6 h-6 rounded-full transition-transform border ${borderColor === color ? 'ring-2 ring-offset-2 dark:ring-offset-gray-700 ring-blue-500' : 'border-gray-300'}`} style={{ backgroundColor: color }}></button>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="text-xs px-3 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600">Cancel</button>
        <button type="submit" className="text-xs px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700">{isEditing ? 'Save' : 'Add Task'}</button>
      </div>
    </form>
  );
};

const AddTaskModal = ({ groupId, memberId, tier, onClose }) => {
  const handleSave = async (data) => {
    await addDoc(collection(db, "groups", groupId, "tasks"), { ...data, assignedTo: memberId, isComplete: false, createdAt: Timestamp.now() });
    onClose();
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Add New Task</h2>
        <TaskForm onSave={handleSave} onCancel={onClose} tier={tier} />
      </div>
    </div>
  );
};

const EditTaskForm = ({ task, groupId, tier, onCancel }) => {
  const handleSave = async (data) => {
    await updateDoc(doc(db, "groups", groupId, "tasks", task.id), data);
    onCancel();
  };
  const handleDelete = async () => {
    if (!window.confirm("Delete this task permanently?")) return;
    await deleteDoc(doc(db, "groups", groupId, "tasks", task.id));
    onCancel();
  };
  return (
    <div className="relative">
      <TaskForm onSave={handleSave} onCancel={onCancel} initialData={task} isEditing={true} tier={tier}/>
      <button onClick={handleDelete} title="Delete Task" className="absolute top-2 right-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full">
        <Trash2 size={14} />
      </button>
    </div>
  );
};

// --- Invite Member Modal ---
const InviteMemberModal = ({ group, user, tier, tierLimits, onClose }) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isInviting, setIsInviting] = useState(false);

// REPLACE the entire handleInvite in InviteMemberModal with this:
const handleInvite = async (e) => {
  e.preventDefault();
  setMessage('');
  setIsInviting(true);

  try {
    const callSendInvite = httpsCallable(functions, 'sendGroupInvite');
    await callSendInvite({
      email: email.trim(),
      groupId: group.id,
      groupName: group.groupName,
    });

    setMessage(`Successfully sent an invitation to ${email.trim()}!`);
    setEmail('');
  } catch (error) {
    console.error("Error sending invitation via function:", error);

    // Friendly messages
    const code = error?.code || '';
    if (code === 'permission-denied') {
      setMessage("This user has blocked invitations from you.");
    } else if (code === 'unauthenticated') {
      setMessage("Please sign in again and try sending the invite.");
    } else if (code === 'invalid-argument') {
      setMessage("Missing info for invite. Check email and try again.");
    } else {
      setMessage("Could not send email invite. Please try again later.");
    }
  } finally {
    setIsInviting(false);
  }
};


  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 w-full max-w-md">
        <h2 className="text-2xl font-bold mb-4">Invite Member</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Enter the email address of the person you want to invite. They will be prompted to accept or deny the invitation on their dashboard.</p>
        <form onSubmit={handleInvite}>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="member@example.com" className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required/>
          {message && <p className={`text-sm mt-3 text-center ${message.startsWith('Error') ? 'text-red-500' : 'text-green-500'}`}>{message}</p>}
          <div className="flex justify-end gap-4 mt-6">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">Close</button>
            <button type="submit" disabled={isInviting} className="px-4 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:bg-green-400 transition-colors">
              {isInviting ? 'Inviting...' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Upgrade Modal ---
// 2-tier model: "Clarity" (free) and "Momentum" (paid, $9.99/yr).
// NOTE: Momentum is sold as a single annual plan to match the iOS app.
//
// IMPORTANT — Stripe price: the old Momentum annual price was $19.99/yr.
// Stripe price amounts are immutable, so to charge $9.99/yr you must create a
// NEW recurring price ($9.99/yr) in the Stripe Dashboard and paste its id below.
// Until then this still points at the legacy $19.99/yr price.
const MOMENTUM_ANNUAL_PRICE_ID = "price_1S0eRWPAasMQxlb6XidEeUdS"; // TODO: replace with new $9.99/yr price id

const UpgradeModal = ({ user, onClose }) => {
  const handleUpgradeClick = async (priceId) => {
    const createStripeCheckout = httpsCallable(functions, 'createStripeCheckout');
    try {
      const result = await createStripeCheckout({ priceId });
      const { url } = result.data; window.location.assign(url);
    } catch (e) {
      console.error("Stripe checkout error:", e);
      alert("Could not create payment session. Please try again.");
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 w-full max-w-3xl">
        <h2 className="text-3xl font-bold text-center mb-6">Upgrade Your Plan</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 border rounded-lg">
            <h3 className="text-xl font-semibold">Clarity</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Your current plan</p>
            <p className="text-3xl font-bold my-4">$0</p>
            <ul className="space-y-2 text-sm">
              <li><Check size={16} className="inline mr-2 text-green-500"/>Up to 2 groups</li>
              <li><Check size={16} className="inline mr-2 text-green-500"/>1 invite per group</li>
              <li><Check size={16} className="inline mr-2 text-green-500"/>3 tasks per section</li>
              <li><Check size={16} className="inline mr-2 text-green-500"/>On-device daily reminder</li>
              <li><Check size={16} className="inline mr-2 text-green-500"/>Ad-supported</li>
            </ul>
          </div>
          <div className="p-6 border-2 border-purple-500 rounded-lg relative">
            <span className="absolute top-0 -translate-y-1/2 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">RECOMMENDED</span>
            <h3 className="text-xl font-semibold">Momentum</h3>
            <p className="text-3xl font-bold my-4">$9.99<span className="text-base font-normal">/yr</span></p>
            <ul className="space-y-2 text-sm">
              <li><Check size={16} className="inline mr-2 text-green-500"/>Up to 15 groups</li>
              <li><Check size={16} className="inline mr-2 text-green-500"/>10 invites per group</li>
              <li><Check size={16} className="inline mr-2 text-green-500"/>20 tasks per section</li>
              <li><Check size={16} className="inline mr-2 text-green-500"/>No ads</li>
              <li><Check size={16} className="inline mr-2 text-green-500"/>Daily email reminders</li>
              <li><Check size={16} className="inline mr-2 text-green-500"/>Calendar integration</li>
              <li><Check size={16} className="inline mr-2 text-green-500"/>Full color palette</li>
              <li><Check size={16} className="inline mr-2 text-green-500"/>Background customization</li>
              <li><Check size={16} className="inline mr-2 text-green-500"/>Downloadable Gantt charts</li>
            </ul>
            <button onClick={() => handleUpgradeClick(MOMENTUM_ANNUAL_PRICE_ID)} className="w-full mt-6 py-2 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Upgrade to Momentum</button>
          </div>
        </div>
        <div className="text-center mt-6">
          <button onClick={onClose} className="text-sm text-gray-500 hover:underline">Maybe later</button>
        </div>
      </div>
    </div>
  );
};

// --- Customize Modal ---
const CustomizeModal = ({ user, groupId, groupName, currentBackgroundPreference, onClose }) => {
  const [selectedPreference, setSelectedPreference] = useState(currentBackgroundPreference || 'default');
  const [isSaving, setIsSaving] = useState(false);

  const handleSavePreference = async () => {
    if (!groupId) {
      console.error("CustomizeModal: missing groupId");
      return;
    }
    setIsSaving(true);
    try {
      // Per-user, per-group: writes a single map field key. Other groups' prefs untouched.
      // Field name `groupBackgroundPreferences` matches iOS for cross-client sync.
      await updateDoc(doc(db, "users", user.uid), {
        [`groupBackgroundPreferences.${groupId}`]: selectedPreference,
      });
      onClose();
    } catch (e) {
      console.error("Update background error:", e);
    } finally {
      setIsSaving(false);
    }
  };

  // Build the CSS style for a thumbnail based on the option type.
  // Image URLs MUST be wrapped in url(...) for backgroundImage CSS to render.
  const styleForOption = (option) => {
    if (option.type === 'color') return { backgroundColor: option.value };
    if (option.type === 'image') {
      return {
        backgroundImage: `url(${option.value})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    if (option.type === 'gradient') return { backgroundImage: option.value };
    // 'default' or unknown
    return {};
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-1">Customize Background</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          For <span className="font-semibold">{groupName}</span> only. Other groups keep their own backgrounds.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          {BACKGROUND_OPTIONS.map(option => {
            const isSelected = selectedPreference === option.value;
            const isDefault = option.type === 'default';
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setSelectedPreference(option.value)}
                aria-label={option.name || 'Background option'}
                className={`relative w-24 h-24 rounded-lg transition-transform transform hover:scale-105 overflow-hidden border border-gray-200 dark:border-gray-700 flex items-center justify-center ${
                  isSelected ? 'ring-4 ring-offset-2 dark:ring-offset-gray-800 ring-blue-500' : ''
                } ${isDefault ? 'bg-white dark:bg-gray-700' : ''}`}
                style={styleForOption(option)}
              >
                {isDefault && <RefreshCw size={22} className="text-gray-500" />}
                {/* Always-visible label with a strong dark gradient strip at the bottom
                    so white text reads clearly on EVERY thumbnail — light, dark, or photo. */}
                {!isDefault && option.name && (
                  <span
                    className="absolute bottom-0 left-0 right-0 text-white text-[11px] font-semibold tracking-wide text-center py-1.5 px-2 truncate"
                    style={{
                      background:
                        'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0) 100%)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                    }}
                  >
                    {option.name}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex justify-end gap-4 mt-8">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSavePreference}
            disabled={isSaving}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:bg-blue-400"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Settings Modal ---
const SettingsModal = ({ user, userData, onClose }) => {
  const [name, setName] = useState(userData.name);
  const [remindersEnabled, setRemindersEnabled] = useState(
    userData.dailyRemindersEnabled || false
  );
  const [savingReminders, setSavingReminders] = useState(false);
  const tier = userData.subscriptionTier || 'free';
  const remindersUnlocked = tier !== 'free';

  const handleNameChange = async (e) => {
    e.preventDefault();
    try { await updateDoc(doc(db, "users", user.uid), { name }); alert("Name updated successfully!"); }
    catch (e) { console.error("Update name error:", e); alert("Could not update name. Please try again."); }
  };

  const handleToggleReminders = async () => {
    if (!remindersUnlocked) return; // gated to paid tiers
    const newValue = !remindersEnabled;
    setRemindersEnabled(newValue);
    setSavingReminders(true);
    try {
      const updates = { dailyRemindersEnabled: newValue };
      // When enabling, opportunistically capture the user's timezone + default
      // reminder hour if they don't already have one stored. This makes existing
      // accounts (created before per-user TZ) work without manual migration.
      // Field name `reminderTimeZone` matches iOS for cross-client compatibility.
      if (newValue) {
        if (!userData.reminderTimeZone && !userData.timezone) {
          try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (tz) updates.reminderTimeZone = tz;
          } catch (e) { /* server falls back to America/New_York */ }
        }
        if (!Number.isInteger(userData.reminderHour)) {
          updates.reminderHour = 6;
        }
      }
      await updateDoc(doc(db, "users", user.uid), updates);
    } catch (e) {
      console.error("Update reminder setting error:", e);
      setRemindersEnabled(!newValue);
      alert("Could not update reminder setting. Please try again.");
    } finally {
      setSavingReminders(false);
    }
  };

  const handleManageSubscription = async () => {
    const createStripePortalLink = httpsCallable(functions, 'createStripePortalLink');
    try {
      const result = await createStripePortalLink();
      const { url } = result.data; window.location.assign(url);
    } catch (e) {
      console.error("Stripe portal error:", e);
      alert("Could not open subscription manager. Please try again later.");
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm("Are you sure you want to permanently delete your account? This action cannot be undone.")) return;
    const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount');
    try { await deleteUserAccount(); }
    catch (e) { console.error("Delete account error:", e); alert("Could not delete account. Please try again."); }
  };

  // Show the user-facing label for their reminder time and timezone.
  const reminderHour = Number.isInteger(userData.reminderHour) ? userData.reminderHour : 6;
  const reminderHourLabel = (() => {
    const h12 = ((reminderHour + 11) % 12) + 1;
    const ampm = reminderHour < 12 ? 'AM' : 'PM';
    return `${h12}:00 ${ampm}`;
  })();
  // Read iOS-compatible field first, fall back to legacy `timezone` for accounts
  // created before the rename, then to the browser-detected zone as final fallback.
  const reminderTz =
    userData.reminderTimeZone ||
    userData.timezone ||
    (() => {
      try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'America/New_York'; }
    })();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">Settings</h2>
        <form onSubmit={handleNameChange} className="mb-6">
          <label className="block text-sm font-medium mb-2">Display Name</label>
          <div className="flex gap-2">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <button type="submit" className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">Save</button>
          </div>
        </form>

        {/* Current plan */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Plan</label>
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{displayTierName(tier)}</p>
            {tier === 'free' && (
              <span className="text-xs text-gray-500 dark:text-gray-400">Free</span>
            )}
          </div>
        </div>

        {/* Daily Reminders — one user-level toggle covering tasks across all groups. */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Daily Reminders</label>
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="pr-3">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                Email me about tasks
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {remindersUnlocked
                  ? `Sent at ${reminderHourLabel} (${reminderTz}). One email per day covering overdue and due-today tasks from all your groups.`
                  : 'Daily email reminders are part of Momentum. Clarity includes the on-device reminder only — upgrade to Momentum to also get the email.'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={remindersEnabled}
              onClick={handleToggleReminders}
              disabled={!remindersUnlocked || savingReminders}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                remindersEnabled && remindersUnlocked
                  ? 'bg-blue-600'
                  : 'bg-gray-300 dark:bg-gray-600'
              } ${!remindersUnlocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  remindersEnabled && remindersUnlocked ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>

        {userData.subscriptionTier !== 'free' && (
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Subscription</label>
            <button onClick={handleManageSubscription} className="w-full py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">Manage Subscription</button>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2 text-red-600 dark:text-red-400">Danger Zone</label>
          <button onClick={handleDeleteAccount} className="w-full py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete Account</button>
        </div>

        <div className="text-center mt-8">
          <button onClick={onClose} className="text-sm text-gray-500 hover:underline">Close</button>
        </div>
      </div>
    </div>
  );
};
// --- Gantt Modal (stacked rows, overdue icon + red border) ---
const GanttModal = ({ groupName, members, tasks, onClose }) => {
  // Layout constants
  const windowDays   = 30;    // show today..today+29
  const dayWidth     = 22;    // px per day
  const barHeight    = 22;    // px bar height
  const barGap       = 6;     // px gap between bars
  const headerHeight = 26;    // px per-member header
  const leftPad      = 180;   // px for member names
  const topPad       = 44;    // px for day labels
  const rightPad     = 20;    // px margin
  const bottomPad    = 28;    // px margin
  const msPerDay     = 24 * 60 * 60 * 1000;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Build bars per member in the same order as your UI (using sortTasks)
  const perMemberBars = members.map((m) => {
    const memberTasks = sortTasks(
      tasks.filter(
        (t) => t.assignedTo === m.id && !t.isComplete && t.dueDate?.toDate
      )
    );

    const bars = memberTasks.map((t) => {
      const due = t.dueDate.toDate();
      due.setHours(0, 0, 0, 0);

      const created = t.createdAt?.toDate ? t.createdAt.toDate() : new Date(due);
      created.setHours(0, 0, 0, 0);

      const isOverdue = due < today;

      // Overdue → extend to today + 7
      const end = new Date(isOverdue ? today.getTime() + 7 * msPerDay : due.getTime());
      end.setHours(0, 0, 0, 0);

      // Day offsets relative to today
      const startDay = Math.floor((created - today) / msPerDay);
      const endDay   = Math.floor((end     - today) / msPerDay);

      // Clamp to window
      const s = Math.max(0, Math.min(windowDays - 1, startDay));
      const e = Math.max(0, Math.min(windowDays - 1, endDay));
      if (e < 0 || s > windowDays - 1) return null; // fully outside

      return {
        label: t.taskName || 'Task',
        color: t.borderColor || '#4F46E5',
        startDay: s,
        widthDays: Math.max(1, e - s + 1),
        isOverdue,
      };
    }).filter(Boolean);

    return { member: m, bars };
  });

  // Compute dynamic height by walking through members and their bars
  let svgWidth  = leftPad + windowDays * dayWidth + rightPad;
  let svgHeight = topPad + bottomPad;
  let currentY = topPad;

  const dayX = (d) => leftPad + d * dayWidth;

  // Precompute day labels
  const dayLabels = Array.from({ length: windowDays }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return { i, label: `${d.getMonth() + 1}/${d.getDate()}`, isToday: i === 0 };
  });

  // Record layout (member headers + bar rects) as a render plan
  const renderPlan = [];
  perMemberBars.forEach(({ member, bars }) => {
    // Member header
    renderPlan.push({
      type: 'memberHeader',
      y: currentY,
      name: member.name || 'Member',
    });
    currentY += headerHeight;

    // Bars (stacked, same order as task list)
    bars.forEach((b) => {
      renderPlan.push({
        type: 'bar',
        y: currentY,
        startX: dayX(b.startDay) + 1,
        width: Math.max(3, b.widthDays * dayWidth - 2),
        color: b.color,
        label: b.label,
        isOverdue: b.isOverdue,
      });
      currentY += barHeight + barGap;
    });

    // Light separator after this member section (only if there were bars)
    const hasBars = bars.length > 0;
    renderPlan.push({
      type: 'separator',
      y: currentY - (hasBars ? barGap : 0) + 4,
    });
  });

  svgHeight = Math.max(svgHeight, currentY + bottomPad);

  // Download the SVG as PNG
  const downloadPNG = () => {
    const svgEl = document.getElementById('gantt-svg');
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = svgWidth;
      canvas.height = svgHeight;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);

      canvas.toBlob((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${groupName}_gantt.png`;
        a.click();
        URL.revokeObjectURL(a.href);
      }, 'image/png');
    };
    img.src = url;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 w-full max-w-5xl">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold">Gantt (created → due, next 30 days)</h2>
          <div className="flex gap-2">
            <button
              onClick={downloadPNG}
              className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
            >
              Download PNG
            </button>
            <button
              onClick={onClose}
              className="px-3 py-2 text-sm border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>

        <div className="overflow-auto border rounded">
          <svg
            id="gantt-svg"
            width={svgWidth}
            height={svgHeight}
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Background */}
            <rect x="0" y="0" width={svgWidth} height={svgHeight} fill="#ffffff" />

            {/* Day grid & labels spanning entire chart */}
            {dayLabels.map(({ i, label, isToday }) => (
              <g key={`col-${i}`}>
                <line
                  x1={dayX(i)}
                  y1={topPad - 18}
                  x2={dayX(i)}
                  y2={svgHeight - 14}
                  stroke={isToday ? "#ef4444" : "#e5e7eb"}
                  strokeWidth={isToday ? 2 : 1}
                />
                <text
                  x={dayX(i) + dayWidth / 2}
                  y={topPad - 22}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#374151"
                >
                  {label}
                </text>
              </g>
            ))}

            {/* Render plan */}
            {renderPlan.map((item, idx) => {
              if (item.type === 'memberHeader') {
                return (
                  <g key={`mh-${idx}`}>
                    <text
                      x={leftPad - 12}
                      y={item.y + headerHeight * 0.72}
                      textAnchor="end"
                      fontSize="12"
                      fill="#111827"
                    >
                      {item.name}
                    </text>
                  </g>
                );
              }
              if (item.type === 'bar') {
                // Reserve room for the warning icon if overdue
                const iconSize = item.isOverdue ? 12 : 0;
                const iconPad  = item.isOverdue ? 4 : 0;
                const textX    = item.startX + 6 + iconSize + iconPad;

                return (
                  <g key={`bar-${idx}`}>
                    {/* main bar with optional red border when overdue */}
                    <rect
                      x={item.startX}
                      y={item.y}
                      width={item.width}
                      height={barHeight}
                      rx="4"
                      fill={item.color}
                      opacity="0.9"
                      stroke={item.isOverdue ? "#ef4444" : "none"}
                      strokeWidth={item.isOverdue ? 2 : 0}
                    />

                    {/* warning triangle + exclamation for overdue tasks */}
                    {item.isOverdue && (
                      <g>
                        {/* triangle (point up) */}
                        <polygon
                          points={[
                            `${item.startX + 6},${item.y + barHeight - 4}`,
                            `${item.startX + 6 + iconSize/2},${item.y + 4}`,
                            `${item.startX + 6 + iconSize},${item.y + barHeight - 4}`
                          ].join(" ")}
                          fill="#ef4444"
                          stroke="#b91c1c"
                          strokeWidth="1"
                        />
                        {/* exclamation mark inside triangle */}
                        <text
                          x={item.startX + 6 + iconSize/2}
                          y={item.y + barHeight/2 + 1}
                          textAnchor="middle"
                          fontSize="10"
                          fontWeight="700"
                          fill="#ffffff"
                        >
                          !
                        </text>
                      </g>
                    )}

                    {/* label */}
                    <text
                      x={textX}
                      y={item.y + barHeight * 0.68}
                      fontSize="11"
                      fill="#ffffff"
                    >
                      {item.label}
                    </text>
                  </g>
                );
              }
              // separator line
              return (
                <line
                  key={`sep-${idx}`}
                  x1={leftPad}
                  y1={item.y}
                  x2={svgWidth - 10}
                  y2={item.y}
                  stroke="#f3f4f6"
                  strokeWidth="1"
                />
              );
            })}
          </svg>
        </div>

        <p className="mt-2 text-xs text-gray-500">
          Completed tasks are hidden. Overdue tasks extend to today + 7 days and display a warning icon with a red border.
        </p>
      </div>
    </div>
  );
};

export default App;
